import React, { useState } from "react";
import {
  TbChevronRight, TbUser, TbSend, TbUsers, TbBrush, TbAdjustments, TbPlug,
} from "react-icons/tb";
import "./Settings.css";

// Administration: everything configurable, in one place.
//
// Settings used to be a rail area. The rail is for the places someone works all day —
// organizations, the scanner, report templates, business reviews — and configuration is
// not one of them. It was also reachable twice over once the account panel existed,
// which is two doors into the same rooms.
//
// This is a hub, not a container: every card opens the leaf screen at its own address
// (/settings/profile, /settings/branding, ...), so each one can be linked to directly
// and the breadcrumb leads back here.
//
// Two-factor is deliberately absent. ProfileScreen carries the enrolment inline, so a
// separate row would be the same screen twice.
const GROUPS = [
  {
    key: "account",
    label: "Account",
    blurb: "Your sign-in details, and what you have asked us to build.",
    items: [
      { key: "profile", label: "Profile", icon: TbUser, desc: "Sign-in details, two-factor, and password" },
      { key: "requests", label: "Feature requests", icon: TbSend, desc: "Ask for something, and vote on what others asked for" },
    ],
  },
  {
    key: "workspace",
    label: "Workspace admin",
    blurb: "Applies to everyone in this workspace.",
    admin: true,
    items: [
      // Moved in from the Organizations rail area, where it sat beside the client list.
      // It is workspace configuration, and this is where people look for it.
      { key: "admin-users", label: "Admin panel", icon: TbUsers, desc: "Users, roles, and access" },
      { key: "branding", label: "Branding", icon: TbBrush, desc: "Report colours and logo" },
      { key: "thresholds", label: "Risk thresholds", icon: TbAdjustments, desc: "The scoring bands behind every report" },
      { key: "integrations", label: "Integrations", icon: TbPlug, desc: "Connected third-party services" },
    ],
  },
];

const AdministrationScreen = ({ isAdmin, onNavigate }) => {
  const groups = GROUPS.filter((g) => !g.admin || isAdmin);

  // A member sees the Account group alone, and one category down the left is furniture
  // rather than navigation — so the list collapses and the cards stand on their own.
  const [group, setGroup] = useState(groups[0]?.key || "account");
  const current = groups.find((g) => g.key === group) || groups[0];

  return (
    <div className="v2-page">
      <div className="v2-pagehead">
        <div>
          <div className="v2-eyebrow">Workspace administration</div>
          <h2>Administration</h2>
          <p>Everything configurable, in one place.</p>
        </div>
      </div>

      <div className={`v2-admin${groups.length < 2 ? " v2-admin--nonav" : ""}`}>
        {groups.length > 1 && (
          <nav className="v2-admin-nav" aria-label="Settings groups">
            <div className="v2-section-title">Groups</div>
            {groups.map((g) => (
              <button key={g.key} type="button"
                      className={`v2-admin-navitem${g.key === current.key ? " on" : ""}`}
                      aria-current={g.key === current.key ? "page" : undefined}
                      onClick={() => setGroup(g.key)}>
                {g.label}
              </button>
            ))}
          </nav>
        )}

        <div className="v2-admin-body">
          <div>
            <h3 className="v2-admin-title">{current.label}</h3>
            <p className="v2-admin-blurb">{current.blurb}</p>
          </div>

          <div className="v2-admin-cards">
            {current.items.map(({ key, label, icon: Icon, desc }) => (
              <button key={key} type="button" className="v2-admin-card" onClick={() => onNavigate && onNavigate(key)}>
                <span className="v2-admin-card-ic"><Icon size={17} /></span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span className="v2-admin-card-name">{label}</span>
                  <span className="v2-admin-card-desc">{desc}</span>
                </span>
                <TbChevronRight size={16} className="v2-admin-card-go" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdministrationScreen;
